import * as React from "react";
import {ChangeEvent, ReactElement, useEffect, useRef, useState} from "react";
import {IUserData, UsersDic} from "../Interfaces/IUsers";
import {IScopeOverview, TenantLine} from "../Interfaces/interfaces";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {IUserTableData, UserManagementModalMode} from "../../Hub/Interfaces/IUserManagement";
import {transformOdxLayoutRef} from "../../../OdxComponents/odxLayoutRef";
import OdxSelect, {OdxSelectOptionProps} from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import {convertAuthlevelToTenantsystem, loadAllTenantScope} from "../Utils/tenantSystem";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {GetAccessRoles} from "../../Hub/BackendFacade/apiCalls";
import OdxChip from "../../../OdxComponents/NativeOdx/Odx-Chip";
import {
    getDisplayNameFromUserObject,
    getIdFromUserObject,
    getTenantObjectFromUserObject,
    GuidDisplayNameComponent,
    removeDuplicates, simplifyForHtmlId
} from "../../../Utils/transformer";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import {odxIconSets, odxVariants} from "../../../OdxComponents/constants";
import UserManagementModal from "./Modals/UserManagementModal";
import OdxTable, {TableHeaderCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {getConfigDataCall, getUserDataCall} from "../BackendFacade/apiCalls";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";
import {IAppConfig} from "../Interfaces/IConfig";
import {downloadListObjectAsCSV} from "../../../Utils/objectDownload";
import {UserRoles} from "../../../Utils";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";

interface UsersTabProps {
    hubOrIdmsLevel: "Hub" | "IDMS";
}

// We have 3 options to store values like appConfig (which is not related to GUI and just some small bools to check how we process the data, e.g. if tenantManagement is enabled or not):
// Option 1: use Hook (useState): because if we call setAppConfig(newStuff) in line 5 and read appConfig in line 6, it doesnt work and returns null, because we need to "free"/"reset" the callStack for this set/change to apply. Even using await and timeouts doesnt help.
// Option 2: use a local variable inside the component, which will reset on re-render, but all other values, like "isLoaded" (which are hooks). do not reset, causing an incompatibility where isLoaded is true (still) but appConfig is null (because it was reset by the re-render
// Option 3: a global variable, which works simply and wont get reset, but looks a bit ugly.

const UsersTab: React.FC<UsersTabProps> = ({hubOrIdmsLevel}) => {
    const [userData, setUserData] = useState({} as UsersDic);
    const [modalMode, setModalMode] = useState<UserManagementModalMode>(UserManagementModalMode.None);

    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedUserRow, setSelectedUserRow] = useState<IUserData>(null);

    const [listOfEmails, setListOfEmails] = useState<string[]>([]);
    const [tableData, setTableData] = useState<IUserTableData[]>([]);
    //const [appConfig, setAppConfig] = useState<IAppConfig>(null);
    const [isLoadingLocal, setIsLoadingLocal] = useState(true);

    const {setLoading, createToast, createErrorToast, isLoading,} = useMain();

    // filter bar
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [roleOptions, setRoleOptions] = useState<OdxSelectOptionProps[]>([{value: "", label: "Show all"}]);
    const [projectOptions, setProjectOptions] = useState<OdxSelectOptionProps[]>([{value: "", label: "Show all"}]);

    const [appConfig, setAppConfig] = useState<IAppConfig>(undefined);

    // IDMS
    const [scopeOverview, setScopeOverview] = useState<IScopeOverview>({subscopelist: [], scopelist: []});

    // get unique project id for hub user management
    const currentProject = HubPathRouting.currentProject?.id || "null";
    // get IDMS module id for IDMS user management
    const currentModule = HubPathRouting.currentModule?.id;
    const currentConfig = hubOrIdmsLevel === "Hub" ? currentProject : currentModule;
    const baseId = `${hubOrIdmsLevel === "Hub" ? "hub" : "idms"}-users`;
    // INIT
    useModuleViewTracking('idms-userstab')

    useEffect(() => {
        initUsersTab();
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            transformOdxLayoutRef(containerRef.current);
        }
    }, [containerRef, isLoading]);

    // update table when filter or data changes
    useEffect(() => {
        if (!isLoading) {
            const sortedData = filterFullTable();
            setTableData(sortedData);
        }
    }, [selectedRole, selectedProject, userData, isLoading]);

    // ==========================================================================================================
    // ==== INIT, API, DATA =====================================================================================
    // ==========================================================================================================

    useEffect(() => {
        if (!isLoadingLocal) {
            processUserData();
        }
    }, [isLoadingLocal]);

    const initUsersTab = async () => {
        setLoading(true);

        try {
            let promises = [];
            promises.push(getUserData(false));
            if (isIdmsModuleMode()) {
                promises.push(getAppConfig());
            } else {
                // hub
                promises.push(loadAllTenantScope(true, createErrorToast));
            }

            await Promise.all(promises);
        } catch (e) {
            console.error("Error in initUsersTab", e);
            createErrorToast("Error in initUsersTab: " + e.message);
            // go back in browser history
            window.history.back();
        }

        // this order is important: processUserData will call "setUserData", which will trigger the useEffect to update the GUI, but that trigger will only update the GUI if isLoading is false!
        setLoading(false);
        setIsLoadingLocal(false);

        if (!isIdmsModuleMode()) {
            // hub
            // Set values for filter bar options
            const projectsData = await GlobalDictionary.getProjects(); // todo: await this in parallel together with the other 2 promises above
            setProjectOptions([{
                value: "",
                label: "Show all"
            }, ...projectsData.map(project => ({value: project.projectId, label: project.displayName}))]);
        }
    }

    const reloadUserData = async () => {
        setLoading(true);
        setIsLoadingLocal(true);
        await getUserData(true);
        setIsLoadingLocal(false);
        setLoading(false);
    }

    const getUserData = async (force = false) => {
        // use cache
        if (GlobalDictionary.get("users" + currentConfig) && !force) {
            return GlobalDictionary.get("users" + currentConfig);
        }

        let data = isIdmsModuleMode() ?
            await getUserDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id)
            : await GetAccessRoles(currentConfig);

        if (data.success) {
            GlobalDictionary.set("users" + currentConfig, data.data.value);
            return data.data.value;
        }

        createErrorToast("Error getting user data " + data.message);
        throw new Error(data.message);
    };

    /**
     * Get the config data from the backend
     */
    const getAppConfig = async () => { // todo: cache??
        let data = await getConfigDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id);
        if (!data.success) {
            createErrorToast("Error getting config data " + data.message);
            throw new Error(data.message);
        }

        const newAppConfig: IAppConfig = {
            // convert string with true/false to boolean
            IsTenantManagementDisabled: data.data.value.DisableTenantManagement === "true",
            ...data.data.value
        }

        setAppConfig(newAppConfig);
    }

    const processUserData = () => {
        let cleanUsers: UsersDic = {};
        let roleSet = new Set<string>();
        let proj = isIdmsModuleMode() ? "" : currentProject;

        const apiResponseData: IUserData[] = GlobalDictionary.get("users" + currentConfig)
        apiResponseData.forEach((user) => {
            cleanUsers[getIdFromUserObject(user, isIdmsModuleMode())] = user;

            if (hasAnyKindOfTenantManagements()) {
                // check for valid tenantSystem in authlevel first
                if (user.authLevel && !user.authLevel.startsWith("{") && isIdmsModuleMode()) {
                    cleanUsers[getIdFromUserObject(user, isIdmsModuleMode())].tenantSystem = []
                } else {
                    cleanUsers[getIdFromUserObject(user, isIdmsModuleMode())].tenantSystem = convertAuthlevelToTenantsystem(
                        getTenantObjectFromUserObject(user, isIdmsModuleMode()
                        ), proj)

                }

            }
        });

        if (isIdmsModuleMode() && hasAnyKindOfTenantManagements()) {
            let scopesOverview = createListOfSubScopes(cleanUsers)
            setScopeOverview(scopesOverview);
        }

        // used to check for duplicates when adding new users
        const emails = Object.keys(cleanUsers).map(key => cleanUsers[key].mail || cleanUsers[key].userId);
        setListOfEmails(emails);

        setUserData(cleanUsers);

        // add all roles to the set from cleanUsers
        Object.values(cleanUsers).forEach((user) => {
            if (user.tenantSystem) {
                user.tenantSystem.forEach((tenant) => {
                    roleSet.add(tenant.value);
                });
            } else {
                roleSet.add(user.authLevel);
            }
        });
        setRoleOptions([{value: "", label: "Show all"}, ...Array.from(roleSet).map(role => ({
            value: role,
            label: role
        }))]);
    };

    // ==========================================================================================================
    // ==== GUI STUFF, FILTERS, ... =============================================================================
    // ==========================================================================================================

    const applyFilter = (usersDic: UsersDic) => {
        let listOfUserIds = Object.keys(usersDic);

        // filter by role
        if (selectedRole !== "") {
            if (hasAnyKindOfTenantManagements()) {
                listOfUserIds = listOfUserIds.filter(x => usersDic[x].tenantSystem.some(tenant => tenant.value === selectedRole));
            }
            else {
                listOfUserIds = listOfUserIds.filter(x => {
                    return usersDic[x].authLevel === selectedRole;
                })
            }
            }

        // filter by scope in case we have tenants
        if (selectedProject !== "") {
            listOfUserIds = listOfUserIds.filter(x => {
                return usersDic[x].tenantSystem.some(tenant => {
                    const formattedProjectName = tenant.scopes.slice(1).join(" / ");
                    return formattedProjectName === selectedProject;
                });
            });
        }

        return listOfUserIds.map(x => usersDic[x]);
    };

    const filterFullTable = () => {
        let filteredUsers = applyFilter(userData);

        const tableData = filteredUsers.map(user => {
            if (!user) {
                return null;
            }
            const userId = getIdFromUserObject(user, isIdmsModuleMode());
            const displayName = getDisplayNameFromUserObject(user);
            const {
                globalRole,
                globalRoleLabel,
                projectRoles,
                projectRoleLabels
            } = formatRolesWithChip(user.tenantSystem);
            const authLevelLabel = isIdmsModuleMode() && !hasAnyKindOfTenantManagements()
                ? (user.authLevel.startsWith("{") ? "Invalid" : user.authLevel)
                : user.authLevel;

            return {
                user,
                userId,
                displayName,
                authLevelGlobal: globalRole,
                authLevelGlobalLabel: globalRoleLabel,
                authLevelProjects: projectRoles,
                authLevelProjectsLabel: projectRoleLabels.length > 0 ? projectRoleLabels.join(" | ") : "",
                lastLoginDate: convertTimestampToDate(user.lastLogin),
                lastLogin: user.lastLogin,
                authLevel: user.authLevel,
                authLevelLabel
            } as IUserTableData;
        }).filter(Boolean);

        return tableData;
    };

    const formatRolesWithChip = (tenantSystem: TenantLine[] = []): {
        globalRole: ReactElement,
        globalRoleLabel: string,
        projectRoles: ReactElement[],
        projectRoleLabels: string[]
    } => {
        let globalRoleChip: ReactElement = <OdxChip content="N/A" removable={false} size="small"
                                                    variant={odxVariants.secondary}/>;
        let globalRoleLabel = "N/A";
        let projectRoleChips: ReactElement[] = [];
        let projectRoleLabels: string[] = [];

        if (tenantSystem.length > 0) {
            tenantSystem.forEach((tenant) => {
                if (tenant.scopes.length === 1 && tenant.scopes[0] === 'user') {
                    const label = tenant.value || "N/A";
                    globalRoleChip = <OdxChip content={label} removable={false} size="small"
                                              variant={odxVariants.secondary}/>;
                    globalRoleLabel = label;
                } else {
                    let names: JSX.Element[] = [];

                    if (hubOrIdmsLevel === "Hub") {
                        names = tenant.scopes.map((scope, index) => {
                            return <GuidDisplayNameComponent scopes={tenant.scopes} position={index}
                                                             globalLayer={true}/>;
                        });
                    } else {
                        names = tenant.scopes.map((scope, index) => {
                            return <>{scope}</>;
                        });
                    }

                    const roleChip = <OdxChip content={tenant.value} removable={false} size="small"
                                              variant={odxVariants.secondary}/>;
                    projectRoleChips.push(
                        <div key={tenant.id}>
                            {names.slice(1).map((e, index) => {
                                if (index === 0) {
                                    return e;
                                }
                                return <span key={index}> / {e}</span>;
                            })} {" - "}
                            {roleChip}
                        </div>
                    );

                    const scopeLabel = tenant.scopes.slice(1).join(" / ");
                    const label = scopeLabel ? `${scopeLabel} - ${tenant.value}` : tenant.value || "";
                    projectRoleLabels.push(label);
                }
            });
        }

        return {
            globalRole: globalRoleChip,
            globalRoleLabel,
            projectRoles: projectRoleChips,
            projectRoleLabels
        };
    };

    /**
     * Check if the role for IDMS and non tenant management is valid
     */
    const getIdmsRole = (authLevel: string) => {
        // if the authlevel starts with { it is invalid
        if (authLevel.startsWith("{")) {
            return <OdxChip
                content={"Invalid"}
                size="small"
                variant={odxVariants.danger}
                removable={false}
            />
        } else {
            return <OdxChip
                content={authLevel}
                size="small"
                variant={odxVariants.secondary}
                removable={false}
            />
        }
    }

    const getAuthLevelProjects = (dataRow: IUserTableData) => {
        if (isIdmsModuleMode() && !(appConfig?.IsTenantManagementDisabled ?? true)) {
            if (!dataRow.authLevel.startsWith("{")) {
                return <OdxChip
                    content={"Invalid"}
                    size="small"
                    variant={odxVariants.danger}
                    removable={false}
                />
            }
        }
        return dataRow.authLevelProjects
    }

    const getTable = () => {
        if (isLoadingLocal) {
            return <div id={`${baseId}-loading`}>Loading...</div>
        }

        if (tableData.length === 0) {
            return <div id={`${baseId}-empty`}>No data</div>
        }

        let headers: TableHeaderCell[] = [];
        headers.push({name: 'userId', title: 'User Email', sortable: true});

        if (!isIdmsModuleMode() && !HubPathRouting.currentProject) headers.push({
            name: 'authLevelGlobal',
            title: 'Global Role',
            sortable: true
        });

        if (isIdmsModuleMode() && hasAnyKindOfTenantManagements()) headers.push({
            name: 'authLevelGlobal',
            title: 'Global Role',
            sortable: true
        });

        if (hasAnyKindOfTenantManagements()) headers.push({
            name: 'authLevelProjects',
            title: 'Project Roles',
            sortable: false
        });

        if (isIdmsModuleMode() && !hasAnyKindOfTenantManagements()) headers.push({
            name: 'authLevel',
            title: 'Role',
            sortable: true
        });

        headers.push({name: 'lastLogin', title: 'Time of last login', sortable: true, required: true});

        const showAuthLevelGlobal = (!isIdmsModuleMode() && !HubPathRouting.currentProject) || (isIdmsModuleMode() && hasAnyKindOfTenantManagements());

        const showProjects = hasAnyKindOfTenantManagements() || !isIdmsModuleMode();

        const showIdmsRoleNoTM = isIdmsModuleMode() && !hasAnyKindOfTenantManagements();

        return (
            <OdxTable id={`${baseId}-table`} ariaDescribedby="user-management-table" headerData={headers} tableHeight={"100%"}
                      className="w-full h-full">
                {tableData.map((dataRow, index) => (
                    <OdxTableRow key={index} onClick={() => openEditUserModal(dataRow.user)} id={`${baseId}-row-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}`}>
                        <OdxTableCell style={{width: '1%'}} id={`${baseId}-cell-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}-email`}>{getDisplayNameFromUserObject(dataRow.user)}</OdxTableCell>
                        {showAuthLevelGlobal && <OdxTableCell id={`${baseId}-cell-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}-global`}>{dataRow.authLevelGlobal}</OdxTableCell>}
                        {showProjects && <OdxTableCell id={`${baseId}-cell-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}-projects`}>{getAuthLevelProjects(dataRow)}</OdxTableCell>}
                        {showIdmsRoleNoTM && <OdxTableCell id={`${baseId}-cell-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}-role`}>{getIdmsRole(dataRow.authLevel)} </OdxTableCell>}
                        <OdxTableCell id={`${baseId}-cell-${simplifyForHtmlId(getIdFromUserObject(dataRow.user, isIdmsModuleMode()))}-last-login`}>{dataRow.lastLoginDate}</OdxTableCell>
                    </OdxTableRow>
                ))}
            </OdxTable>
        );
    }

    /**
     * Check if the user has a valid tenantSystem, add the global level if needed
     * @param user
     */
    const checkForValidUser = (user: IUserData) => {
        // if the tenantSystem is active and the authlevel is invalid
        if (isIdmsModuleMode() && hasAnyKindOfTenantManagements()) {
            // check for global level
            if (user.tenantSystem && !user.tenantSystem.some(x => x.scopes.length === 1 && x.scopes[0] === 'user')) {
                user.tenantSystem.unshift({
                    id: user.tenantSystem.length,
                    scopes: ["user"],
                    value: "user",
                    tmp: "",
                    fullscope: "user"
                });

                // parse authlevel to json
                // add the global level
                // convert it back to string
                let authlevel: IUserData = {lastLogin: 0, mail: ""};
                if (user.authLevel.startsWith("{")) {
                    authlevel = JSON.parse(user.authLevel);
                }
                authlevel["user"] = "user" // todo get it from last system.
                user.authLevel = JSON.stringify(authlevel);
            }
        }
        return user
    }

    const openEditUserModal = (user: IUserData) => {
        setSelectedUserRow(checkForValidUser(user));
        setModalMode(UserManagementModalMode.Edit);
    };

    const openAddUserModal = () => {
        setModalMode(UserManagementModalMode.Add);
        setSelectedUserRow(null);
    };

    const onSuccessModal = async () => {
        setModalMode(UserManagementModalMode.None);
        createToast({title: "Success", message: "User data updated successfully", variant: "success"});
        await reloadUserData();
    };

    // ==========================================================================================================
    // ==== UTILS ===============================================================================================
    // ==========================================================================================================

    const createListOfSubScopes = (userData: UsersDic): IScopeOverview => {
        let scopeList: string[] = []
        let subScopes: string[] = []

        Object.values(userData).forEach((val) => {
            if (!val.tenantSystem) {
                return;
            }
            val.tenantSystem.forEach((tenant) => {
                tenant.scopes.forEach((subscope) => {
                    subScopes.push(subscope)
                });

                scopeList.push(tenant.scopes.join('/'))
            })
        });

        // todo: only subscopelist is used?? maybe? not sure
        return {scopelist: removeDuplicates(scopeList), subscopelist: removeDuplicates(subScopes)};
    }

    const convertTimestampToDate = (timestamp: number) => {
        if (!timestamp) {
            return "Never";
        }
        const date = new Date(timestamp * 1000);
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', {month: 'long'});
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${day} ${month} ${year} at ${hours}:${minutes}:${seconds}`;
    };

    const isIdmsModuleMode = () => {
        if (hubOrIdmsLevel === "IDMS") return true;
        else if (hubOrIdmsLevel === "Hub") return false;
        throw new Error("invalid usersTab mode: " + hubOrIdmsLevel);
    }

    const hasAnyKindOfTenantManagements = () => {
        return !isIdmsModuleMode() || !(appConfig?.IsTenantManagementDisabled ?? true);
    }

    const downloadView = () => {
        const data = tableData.map((item) => {
            return {
                UserEmail: item.displayName,
                Role: hasAnyKindOfTenantManagements() ? item.authLevelGlobalLabel : (item.authLevelLabel || item.authLevel),
                LastLogin: item.lastLogin
            };
        });

        const additionalData = "No. of Users: " + tableData.length;
        downloadListObjectAsCSV(data, additionalData, "UserManagement.csv");
    };

    // ==========================================================================================================
    // ==== MAIN PAGE CONTENT ===================================================================================
    // ==========================================================================================================
    const Style: React.CSSProperties = {
        height: 'calc(100vh - 450px)',
        minHeight: '400px',
    }

    return (
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <div className={"overflow-y-scroll h-[calc(100vh-240px)]"} ref={containerRef} id={`${baseId}-scroll`}>
                <OdxAreaHeader
                    text={"User Management"}
                    subTitle={<div id={`${baseId}-count`}>Users: {tableData.length}</div>}
                    odxContent={[
                        <odx-button
                            key="download"
                            variant="accent"
                            type="button"
                            onClick={downloadView}
                            id={`${baseId}-download-btn`}
                        >
                            <odx-icon slot="prefix" name={`${odxIconSets.core}::download`} size="md"></odx-icon>
                            Download User Data
                        </odx-button>,
                        isUserroleSufficient(UserRoles.editor) && (
                            <odx-button
                                key="create"
                                variant="accent"
                                type="button"
                                id={`${baseId}-create-btn`}
                                onClick={openAddUserModal}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Create User
                            </odx-button>
                        )
                    ]}
                    className={'!pl-0 !pr-0'}
                    id={`${baseId}-header`}
                />

                {modalMode != UserManagementModalMode.None && <UserManagementModal
                    mode={modalMode}
                    onClose={() => setModalMode(UserManagementModalMode.None)}
                    onSuccessFunction={onSuccessModal}
                    projectId={currentProject}
                    userRow={selectedUserRow}
                    emailList={listOfEmails}
                    hubOrIdms={hubOrIdmsLevel}
                    appConfig={appConfig}
                    scopeOverviewIdms={scopeOverview}
                />
                }

                <OdxContentBox id={`${baseId}-filters`}>
                    <div className="flex justify-between !mb-4" style={{width: '40%'}} id={`${baseId}-filter-row`}>
                        <OdxSelect
                            options={roleOptions}
                            value={selectedRole}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedRole(e.target.value)}
                            placeholder="Show all"
                            id={`${baseId}-role-filter`}
                            label="Role"
                            className="flex-1 !mr-2"
                        />
                        {!HubPathRouting.currentProject && <OdxSelect
                            options={projectOptions}
                            value={selectedProject}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedProject(e.target.value)}
                            placeholder="Show all"
                            label="Project"
                            className="flex-1"
                            id={`${baseId}-project-filter`}
                        />}
                    </div>
                    <div style={Style} id={`${baseId}-table-wrapper`}>
                        {getTable()}

                    </div>

                </OdxContentBox>
            </div>
        </div>
    );
}

export default UsersTab;
